import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getRecentTransactions, Transaction } from 'services/admin/adminService';
import Spinner from 'components/common/Spinner';
import { useTheme } from 'contexts/ThemeContext';
import { AlertTriangle, Inbox, CheckCircle, XCircle, Clock } from 'lucide-react';

interface TransactionsTableProps {
  limit?: number;
}

export function TransactionsTable({ limit = 10 }: TransactionsTableProps) {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);

  const tableHeaderBg = isDark ? '#1D293D' : '#F8FAFC';
  const tableBorderColor = isDark ? '#314158' : '#E2E8F0';
  const titleColor = isDark ? '#E2E8F0' : '#314158';
  const subtitleColor = isDark ? '#90A1B9' : '#62748E';
  const tableRowHoverBg = isDark ? '#162030' : '#F8FAFC';
  const textPrimary = isDark ? '#E2E8F0' : '#1E293B';
  const textSecondary = isDark ? '#90A1B9' : '#64748B';
  const errorText = isDark ? '#F87171' : '#DC2626';

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        setLoading(true);
        const data = await getRecentTransactions(limit);
        setTransactions(data.transactions);
        setError(null);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [limit]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Spinner size="medium" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-sm" style={{ color: errorText }}>
        <AlertTriangle size={20} />
        <p>{error}</p>
      </div>
    );
  }

  if (transactions.length === 0) {
    return (
      <div className="text-center py-8" style={{ color: subtitleColor }}>
        <Inbox size={28} />
        <p className="text-sm">{t('admin.transactions.noTransactions', 'No transactions found')}</p>
      </div>
    );
  }

  const getStatusStyle = (status: string): React.CSSProperties => {
    switch (status) {
      case 'completed':
        return { background: isDark ? '#022C22' : '#DCFCE7', color: isDark ? '#5EE9B5' : '#15803D' };
      case 'failed':
        return { background: isDark ? '#3B1010' : '#FEE2E2', color: isDark ? '#F87171' : '#DC2626' };
      case 'pending':
        return { background: isDark ? '#3B2F08' : '#FEF9C3', color: isDark ? '#FACC15' : '#A16207' };
      case 'cancelled':
        return { background: isDark ? '#1D293D' : '#F1F5F9', color: isDark ? '#90A1B9' : '#475569' };
      default:
        return { background: isDark ? '#1D293D' : '#F1F5F9', color: isDark ? '#90A1B9' : '#475569' };
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const formatCurrency = (amount: number, currency: string = 'EUR') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead style={{ background: tableHeaderBg, borderBottom: '1px solid ' + tableBorderColor }}>
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: subtitleColor }}>
              {t('admin.transactions.invoice', 'Invoice')}
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: subtitleColor }}>
              {t('admin.transactions.user', 'User')}
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: subtitleColor }}>
              {t('admin.transactions.amount', 'Amount')}
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: subtitleColor }}>
              {t('admin.transactions.plan', 'Plan')}
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: subtitleColor }}>
              {t('admin.transactions.date', 'Date')}
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: subtitleColor }}>
              {t('admin.transactions.status', 'Status')}
            </th>
          </tr>
        </thead>
        <tbody style={{ background: 'transparent' }}>
          {transactions.map((transaction) => (
            <tr
              key={transaction.id}
              onMouseEnter={() => setHoveredRow(transaction.id)}
              onMouseLeave={() => setHoveredRow(null)}
              style={{
                background: hoveredRow === transaction.id ? tableRowHoverBg : 'transparent',
                borderBottom: '1px solid ' + tableBorderColor,
                transition: 'background-color 0.15s'
              }}
              className="transition-colors"
            >
              <td className="px-4 py-3 whitespace-nowrap">
                <div className="text-sm font-medium" style={{ color: titleColor }}>{transaction.invoiceNumber}</div>
                {transaction.transactionId && (
                  <div className="text-xs" style={{ color: subtitleColor }}>{transaction.transactionId.substring(0, 12)}...</div>
                )}
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                <div className="text-sm font-medium" style={{ color: titleColor }}>{transaction.user?.name || 'N/A'}</div>
                <div className="text-xs" style={{ color: subtitleColor }}>{transaction.user?.email || 'N/A'}</div>
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                <div className="text-sm font-semibold" style={{ color: titleColor }}>
                  {formatCurrency(transaction.amount || 0, transaction.currency || 'EUR')}
                </div>
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                <span className="text-sm" style={{ color: textPrimary }}>{transaction.subscriptionType || 'N/A'}</span>
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                <div className="text-sm" style={{ color: titleColor }}>{transaction.date ? formatDate(transaction.date) : 'N/A'}</div>
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                <span
                  style={{
                    ...getStatusStyle(transaction.status || 'unknown'),
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '2px 8px',
                    fontSize: '12px',
                    fontWeight: 500,
                    borderRadius: '9999px'
                  }}
                >
                  {transaction.status === 'completed' && <CheckCircle size={12} style={{ marginRight: 4 }} />}
                  {transaction.status === 'failed' && <XCircle size={12} style={{ marginRight: 4 }} />}
                  {transaction.status === 'pending' && <Clock size={12} style={{ marginRight: 4 }} />}
                  {transaction.status || 'unknown'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
